/**
 * OperationsPage — live data only. /api/v1/dashboard/operations.
 *
 * Field names mapped 1:1 against the backend response shape (see
 * routes/dashboard.routes.js operationsHandler).
 *
 * Concept reminder: celebrity_slot_booking is a legacy table name from before
 * the direct-call migration. Fancall has NO booking flow — fans call creators
 * directly, on demand. The table now holds two kinds of rows:
 *   • status='available' → creator availability slots (~83% of rows in prod) —
 *     not call attempts
 *   • everything else    → real direct-call attempts (completed/missed/failed)
 * The dashboard surfaces both numbers so "total slot rows" doesn't get
 * misread as "calls". Completion rate is computed against attempts, not slots.
 */
import React from "react";
import { PageGuide } from "./PageGuide.v2";
import { tokensV2 as T } from "./tokens.v2";
import { Tile } from "./Tile.v2";
import { useDashboardPage, formatNum, formatPct, formatSeconds, formatINR } from "./useDashboardPage.v2";
import { rangeWordFor } from "./rangeWord.v2";
import { PayoutQueueCard } from "./InsightsCards.v2";
import { LoadingShell, EmptyError } from "./PageState.v2";

export function OperationsPage({ range, rangeId }) {
  const { data, loading, error, reload } = useDashboardPage("operations", range, rangeId);
  if (loading) return <LoadingShell tiles={4} />;
  if (!data) return <EmptyError title="Couldn't load Operations" error={error} onRetry={reload} />;

  // Defensive fallbacks: backend response shape can drift if safe() in the
  // route handler returns an empty fallback for any sub-section. Render
  // empty-state values instead of hard-crashing on undefined.member access.
  const h = data.hero || {};
  const b = data.bookings || {};
  const q = data.quality || {};
  const rq = data.refundQueue || {};
  const s = data.support || {};
  const attemptsTodayV  = h.attemptsToday  || {};
  const completedTodayV = h.completedToday || {};
  const avgRatingV      = h.avgRatingWindow || {};
  const avgResponseV    = h.avgResponseWindow || {};

  const attemptsTotal = b.attempts || 0;
  const completedTotal = q.completed || 0;
  const word = rangeWordFor(rangeId);
  const windowWord = word; // alias

  const heroTiles = [
    {
      label: "Attempts today", icon: "T", variant: "muted-orange",
      value: formatNum(attemptsTodayV.value),
      subValue: `${formatNum(completedTodayV.value)} completed today`,
      title:
        `Call attempts created today (excludes creator availability slots which\n` +
        `aren't call attempts). Anchored to literal today — does not react to\n` +
        `the date pill.`,
    },
    {
      label: "Avg star rating", icon: "S", variant: "muted-lime",
      value: (avgRatingV.value || 0).toFixed(2),
      subValue: `out of 5 · ${formatNum(avgRatingV.sampleSize)} ratings ${windowWord}`,
      title:
        `Average star rating from completed calls (window-scoped).\n` +
        `Sample size shown so a 5★ from 1 rating doesn't read as solid.`,
    },
    {
      label: "Avg response time", icon: "R", variant: "muted-violet",
      value: formatSeconds(avgResponseV.value),
      subValue: `creator pickup · ${formatNum(avgResponseV.sampleSize)} answered ${windowWord}`,
      title:
        `Time between ringing-start and call-answer.\n` +
        `Only counts answered calls (call_start_time > ringing_start_time).`,
    },
    {
      label: "Refund queue", icon: "Q", variant: "muted-magenta",
      value: formatNum(rq.pendingCount),
      subValue: `${formatINR(rq.pendingValue)} · ${formatNum(rq.overdue24h)} overdue 24h`,
      title:
        `Live refund queue (no date filter):\n` +
        `  Pending count:    ${formatNum(rq.pendingCount)}\n` +
        `  Pending value:    ${formatINR(rq.pendingValue)}\n` +
        `  Overdue (>24h):   ${formatNum(rq.overdue24h)}\n` +
        `  Total open (incl. processing): ${formatNum(rq.totalOpen)}`,
    },
  ];

  // Slot/attempt row — explicitly separates availability slots from real
  // direct-call attempts. (Fancall has no booking flow; fans call directly.
  // The legacy table name `celebrity_slot_booking` is just history.)
  const bookingTiles = [
    {
      label: "Total slot rows", icon: "S", variant: "muted-orange",
      value: formatNum(b.total),
      subValue: `${formatNum(b.availability)} availability slots · ${formatNum(b.attempts)} call attempts`,
      title:
        `celebrity_slot_booking row count for the selected window. Fancall\n` +
        `has no booking flow — fans call creators directly. This table now\n` +
        `holds two kinds of rows:\n` +
        `  • Availability slots: creators marking themselves available\n` +
        `    (NOT call attempts — often ~83% of rows)\n` +
        `  • Call attempts: real direct-call requests (any status except 'available')\n` +
        `Completion rate uses attempts as the denominator, not slot rows.`,
    },
    {
      label: "Call attempts", icon: "C", variant: "muted-lime",
      value: formatNum(attemptsTotal),
      subValue: `${formatNum(q.audioAttempts)} audio · ${formatNum(q.videoAttempts)} video`,
      title:
        `Real direct-call attempts in the selected window: every fan->creator\n` +
        `call request that did (or tried to) connect. Completed + missed +\n` +
        `failed + rejected + reported + other status — but NOT 'available' slots.`,
    },
    {
      label: "Completed calls", icon: "✓", variant: "muted-electric",
      value: formatNum(completedTotal),
      subValue: `${formatPct(q.completionRate)} of attempts`,
      title:
        `Calls that reached 'completed' status (call delivered end-to-end).\n` +
        `Completion rate = completed ÷ attempts, NOT ÷ slot rows.\n` +
        `(Including availability slots in the denominator collapses the rate to\n` +
        `~9% which doesn't reflect actual call quality.)`,
    },
    {
      label: "Avg call duration", icon: "D", variant: "muted-violet",
      value: formatSeconds(q.avgDurationSeconds),
      subValue: `median ${formatSeconds(q.medianDurationSec)} · completed only`,
      title:
        `Average duration across completed calls (callhistory). Audio + video\n` +
        `phases combined when both happened in the same call.`,
    },
  ];

  // Outcome breakdown row — shows where attempts went
  const outcomeTiles = [
    {
      label: "Missed", icon: "M", variant: "muted-yellow",
      value: formatNum(q.missed),
      subValue: "fan/creator didn't pick up",
    },
    {
      label: "Failed", icon: "F", variant: "muted-red",
      value: formatNum(q.failed),
      subValue: "ringing-timeout, app-killed, no-join, etc.",
    },
    {
      label: "Rejected", icon: "X", variant: "muted-magenta",
      value: formatNum(q.rejected),
      subValue: "creator declined ringing",
    },
    {
      label: "Reported / other", icon: "?", variant: "muted-orange",
      value: formatNum((q.reported || 0) + (q.otherStatus || 0)),
      subValue: `${formatNum(q.reported)} reported · ${formatNum(q.otherStatus)} other`,
      title:
        `'Reported' = call flagged during conversation.\n` +
        `'Other' = under-process / available-failed — uncommon edge cases.`,
    },
  ];

  const supportTiles = [
    {
      label: "Open support tickets", icon: "O", variant: "muted-violet",
      value: formatNum(s.openTickets),
      subValue: `${formatNum(s.newInWindow)} new · ${word}`,
      title:
        `Reports with blocked=false in the selected window. (Once a creator\n` +
        `is auto-blocked by the safety system, the report flips to blocked=true\n` +
        `and is no longer "open".)`,
    },
    {
      label: "SLA aging 4–24h", icon: "!", variant: "muted-yellow",
      value: formatNum(s.slaBreached4h),
      subValue: "open and 4–24 hours old",
      title:
        `Open reports created between 4 and 24 hours ago (the warning tier).\n` +
        `Mutually exclusive with the 24h+ tile so the two don't double-count.`,
    },
    {
      label: "SLA breached >24h", icon: "!!", variant: "muted-red",
      value: formatNum(s.slaBreached24h),
      subValue: "open >24 hours unresolved",
      title:
        `Open reports created more than 24 hours ago — critical escalation\n` +
        `tier. If this tile equals total Open, every open ticket is critically\n` +
        `overdue (which is what's currently happening in production).`,
    },
    {
      label: "Safety queue", icon: "!!!", variant: "muted-magenta",
      value: formatNum(s.safetyQueue),
      subValue: "users with 3+ reports",
      title:
        `Open reports where the same subject has been reported 3+ times.\n` +
        `Highest-priority moderation queue — these are likely repeat offenders.`,
    },
  ];

  const csatTiles = [
    {
      label: "CSAT score", icon: "C", variant: "muted-lime",
      value: (s.csat || 0).toFixed(2),
      subValue: "avg star rating · support",
      title: "Customer-satisfaction score from call_ratings (window-scoped). Out of 5.",
    },
    {
      label: "First-call resolution", icon: "F", variant: "muted-electric",
      value: formatPct(s.firstCallResolution),
      subValue: "tickets resolved in 1 contact",
      title: "% of ticket-linked ratings (ticket_id NOT NULL) where the rating was 4★ or higher — proxy for issue resolved on first contact.",
    },
  ];

  return (
    <>
      <div style={{ marginBottom: 16, padding: "0 4px" }}>
        <div style={{ fontSize: 22, fontWeight: 700, color: T.text.primary }}>Operations</div>
        <div style={{ fontSize: 12, color: T.text.muted, marginTop: 4 }}>Call quality · ratings · response time · refunds · support · live from production</div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(240px, 100%), 1fr))", gap: 14, marginBottom: 12 }}>
        {heroTiles.map(t => <Tile key={t.label} {...t} />)}
      </div>

      {/* Pending payout SLA queue — creators waiting >48h are at churn risk */}
      <div style={{ marginBottom: 12 }}>
        <PayoutQueueCard />
      </div>

      <div style={{ marginTop: 14, marginBottom: 6, padding: "0 4px", fontSize: 13, fontWeight: 700, color: T.text.primary }}>Slots vs call attempts · {windowWord}</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(240px, 100%), 1fr))", gap: 14, marginBottom: 12 }}>
        {bookingTiles.map(t => <Tile key={t.label} {...t} />)}
      </div>

      <div style={{ marginTop: 14, marginBottom: 6, padding: "0 4px", fontSize: 13, fontWeight: 700, color: T.text.primary }}>Outcome breakdown · attempts that didn't complete</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(240px, 100%), 1fr))", gap: 14, marginBottom: 12 }}>
        {outcomeTiles.map(t => <Tile key={t.label} {...t} />)}
      </div>

      <div style={{ marginTop: 14, marginBottom: 6, padding: "0 4px", fontSize: 13, fontWeight: 700, color: T.text.primary }}>Support · {windowWord}</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(240px, 100%), 1fr))", gap: 14, marginBottom: 12 }}>
        {supportTiles.map(t => <Tile key={t.label} {...t} />)}
      </div>

      <div style={{ marginTop: 14, marginBottom: 6, padding: "0 4px", fontSize: 13, fontWeight: 700, color: T.text.primary }}>Customer satisfaction · {windowWord}</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(240px, 100%), 1fr))", gap: 14, marginBottom: 12 }}>
        {csatTiles.map(t => <Tile key={t.label} {...t} />)}
      </div>
      <PageGuide groups={OPERATIONS_GUIDE} />
    </>
  );
}

// Definitions for every Operations-page card. celebrity_slot_booking holds BOTH
// availability slots (status='available') and real call attempts; rates use
// attempts, never total rows.
const OPERATIONS_GUIDE = [
  {
    section: "Today / live",
    rows: [
      { card: "Attempts today", means: "Call attempts started today (excludes availability slots).", rule: "celebrity_slot_booking status≠'available', created today." },
      { card: "Completed today", means: "Calls that connected and finished today.", rule: "celebrity_slot_booking status='completed', today." },
      { card: "Refund queue", means: "Live count + value of pending refunds, including overdue (>24h).", rule: "refund_requests status IN (pending, processing) — live, no date filter." },
      { card: "Pending payouts", means: "Live withdrawal queue (creator + brand), bucketed by age.", rule: "withdraw_requests + brand_withdraw_requests, status IN (pending, inprogress)." },
    ],
  },
  {
    section: "Calls (window)",
    rows: [
      { card: "Total slot rows", means: "Every celebrity_slot_booking row — availability slots (~83%) plus real attempts. Shown for transparency.", rule: "count of all rows in window." },
      { card: "Call attempts", means: "Real fan→creator calls (any outcome), excluding availability slots.", rule: "status≠'available', in window." },
      { card: "Completed calls", means: "Calls that connected and finished. Completion rate = completed ÷ attempts (never ÷ total rows).", rule: "status='completed' ÷ attempts." },
      { card: "Avg call duration", means: "Average and median length of completed calls.", rule: "callhistory durations (audio + video phases combined)." },
      { card: "Avg response time", means: "Seconds from ring to creator pickup.", rule: "callhistory: call_start − ringing_start (answered calls only)." },
      { card: "Avg star rating", means: "Average rating from completed calls in the period.", rule: "call_ratings.select_star in window." },
    ],
  },
  {
    section: "Call outcomes",
    rows: [
      { card: "Missed / Failed / Rejected / Reported", means: "How attempts ended (mutually exclusive, sum to attempts). Missed = nobody answered (the biggest bucket); failed = timeout/app-killed; rejected = creator declined; reported = flagged.", rule: "celebrity_slot_booking grouped by status." },
    ],
  },
  {
    section: "Support & CSAT",
    rows: [
      { card: "Open support tickets", means: "Reports not yet auto-blocked, in the period (plus new in window).", rule: "report where blocked=false." },
      { card: "SLA aging / breached / safety queue", means: "Open reports 4–24h old, over 24h old, or with 3+ reports (priority).", rule: "report filtered by age and report_count ≥ 3." },
      { card: "CSAT score", means: "Average support star rating, out of 5.", rule: "call_ratings.select_star in window." },
      { card: "First-call resolution", means: "Share of ticket-linked ratings that are 4★ or higher.", rule: "call_ratings with ticket_id, star≥4 ÷ all ticket-linked ratings." },
    ],
  },
];
